// src/components/Customers/CustomerDetails.jsx
import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Calendar,
  Mail,
  Phone,
  MapPin,
  DollarSign,
  Edit,
  Trash2,
  Plus,
  FileText,
  MessageSquare,
  Activity,
  Shield,
  CheckCircle,
  XCircle,
  User,
  AlertCircle,
  UserPlus,
  Send,
  Clock,
  StickyNote,
  File,
  Tag,
  Zap,
  GitBranch,
  Bot,
  Gift,
  Link,
  Copy,
  Check
} from 'lucide-react';

// Import shadcn components
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Alert, AlertDescription } from '../ui/alert';
import { Separator } from '../ui/separator';
import { Avatar, AvatarFallback } from '../ui/avatar';
import { Label } from '../ui/label';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../ui/table';

// Import custom components
import PageContainer from '../ui/layout/PageContainer/PageContainer';

// Import hooks
import { useCustomers } from '../../hooks/useCustomers';
import { useEntries } from '../../hooks/useEntries';
import { cn } from '../../utils/cn';

const CustomerDetails = () => {
  const { customerId } = useParams();
  const navigate = useNavigate();

  // Get customer data
  const {
    selectedCustomer,
    selectedCustomerLoading,
    selectedCustomerError,
    loadCustomerDetails,
    updateCustomer,
    deleteCustomer,
    clearSelectedCustomer,
    savingCustomer,
    deletingCustomer,
    isConnected
  } = useCustomers();

  // Get customer entries (activity timeline)
  const {
    getEntriesForCustomer,
    isLoadingEntriesForCustomer,
    loadCustomerEntries,
    clearEntriesForCustomer,
    entryTypes,
    loadEntryTypes
  } = useEntries();

  // Local state
  const [activeSection, setActiveSection] = useState('overview');

  // Customer editing state
  const [isEditingCustomer, setIsEditingCustomer] = useState(false);
  const [editedCustomer, setEditedCustomer] = useState({});

  // Canonical: Saving flag watchers
  const prevSavingCustomer = useRef(false);
  const prevDeletingCustomer = useRef(false);

  // Save — exit edit mode on server confirmation
  useEffect(() => {
    if (prevSavingCustomer.current && !savingCustomer) {
      if (isEditingCustomer) {
        setIsEditingCustomer(false);
        setEditedCustomer({});
      }
    }
    prevSavingCustomer.current = savingCustomer;
  }, [savingCustomer, isEditingCustomer]);

  // Delete — navigate away on server confirmation
  useEffect(() => {
    if (prevDeletingCustomer.current && !deletingCustomer) {
      navigate('/customers');
    }
    prevDeletingCustomer.current = deletingCustomer;
  }, [deletingCustomer, navigate]);

  // Section refs for scroll-to functionality
  const sectionRefs = {
    overview: useRef(null),
    appointments: useRef(null),
    invoices: useRef(null),
    activity: useRef(null),
    communication: useRef(null),
    referrals: useRef(null)
  };

  // Store calculated section positions
  const sectionPositions = useRef({});

  // Load customer details and entries when customerId changes or on mount
  // FIXED: Merged duplicate useEffects into one clean effect
  useEffect(() => {
    if (!isConnected || !customerId) return;

    // Check if we need to load - either no data or different customer
    const currentCustomerId = selectedCustomer?.customer?.id;
    const needsLoad = !currentCustomerId || currentCustomerId !== customerId;

    if (needsLoad) {
      console.log('CustomerDetails: Loading customer:', customerId);
      loadCustomerDetails(customerId, {
        includeAppointments: true,
        includeInvoices: true,
        includeStatistics: true
      });

      // Also load entries for the activity timeline
      loadCustomerEntries(customerId);
    }
  }, [isConnected, customerId]); // Minimal dependencies - only triggers on connection or ID change

  // Load entry types once (cached globally)
  useEffect(() => {
    if (isConnected) {
      loadEntryTypes();
    }
  }, [isConnected, loadEntryTypes]);

  // Clear data when component unmounts
  useEffect(() => {
    return () => {
      clearSelectedCustomer();
      if (customerId) {
        clearEntriesForCustomer(customerId);
      }
    };
  }, [customerId, clearSelectedCustomer, clearEntriesForCustomer]);

  // Extract data from backend response
  const customer = useMemo(() => {
    if (!selectedCustomer) return null;

    if (selectedCustomer.customer) {
      return selectedCustomer.customer;
    } else if (selectedCustomer.id || selectedCustomer.customer_id) {
      return selectedCustomer;
    }

    return null;
  }, [selectedCustomer]);

  const appointments = useMemo(() => {
    if (!selectedCustomer) return [];
    return selectedCustomer.appointments ||
           selectedCustomer.appointment_history ||
           selectedCustomer.events ||
           [];
  }, [selectedCustomer]);

  const invoices = useMemo(() => {
    if (!selectedCustomer) return [];
    return selectedCustomer.invoices ||
           selectedCustomer.invoice_history ||
           selectedCustomer.billing_history ||
           [];
  }, [selectedCustomer]);

  // Calculate section positions once when customer loads
  useEffect(() => {
    if (!customer) return;

    const calculatePositions = () => {
      const contentDiv = sectionRefs.overview?.current?.parentElement;
      if (!contentDiv) return;

      const containerTop = contentDiv.scrollTop;
      const positions = {};

      Object.entries(sectionRefs).forEach(([id, ref]) => {
        if (ref.current) {
          const rect = ref.current.getBoundingClientRect();
          const parentRect = contentDiv.getBoundingClientRect();
          positions[id] = rect.top - parentRect.top + containerTop;
        }
      });

      sectionPositions.current = positions;
    };

    setTimeout(calculatePositions, 100);
  }, [customer]);

  // Scroll spy
  useEffect(() => {
    if (!customer) return;

    const contentDiv = sectionRefs.overview?.current?.parentElement;
    if (!contentDiv) return;

    const handleScroll = () => {
      const scrollTop = contentDiv.scrollTop;
      const scrollHeight = contentDiv.scrollHeight;
      const clientHeight = contentDiv.clientHeight;
      const positions = sectionPositions.current;

      const isAtBottom = scrollTop + clientHeight >= scrollHeight - 20;

      if (isAtBottom) {
        setActiveSection(prev => prev !== 'communication' ? 'communication' : prev);
        return;
      }

      const sectionOrder = ['communication', 'activity', 'invoices', 'appointments', 'overview'];

      for (const sectionId of sectionOrder) {
        const sectionTop = positions[sectionId];
        if (sectionTop !== undefined && scrollTop >= sectionTop - 100) {
          setActiveSection(prev => prev !== sectionId ? sectionId : prev);
          break;
        }
      }
    };

    contentDiv.addEventListener('scroll', handleScroll);
    handleScroll();

    return () => {
      contentDiv.removeEventListener('scroll', handleScroll);
    };
  }, [customer]);

  // Scroll to section
  const scrollToSection = (sectionId) => {
    setActiveSection(sectionId);
    sectionRefs[sectionId]?.current?.scrollIntoView({
      behavior: 'smooth',
      block: 'start'
    });
  };

  // Helper functions
  const getInitials = (name) => {
    if (!name) return 'UC';
    return name
      .split(' ')
      .map(n => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(parseFloat(amount || 0));
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  const getConsentMethodDisplay = (method) => {
    const methods = {
      'double_opt_in_sms': 'Double Opt-in (SMS)',
      'admin_manual': 'Manually Added',
      'widget_checkbox': 'Booking Widget',
      'phone_call': 'Phone Call',
      'in_person': 'In Person'
    };
    return methods[method] || method || 'Unknown';
  };

  // Event handlers
  const handleBack = () => {
    navigate('/customers');
  };

  const handleEditCustomer = () => {
    // Enter edit mode and initialize edited fields with current values
    setIsEditingCustomer(true);
    setEditedCustomer({
      name: customerName,
      email: customerEmail,
      phone: customerPhone,
      address: customerAddress
    });
  };

  const handleSaveCustomer = () => {
    // Build updates object with only changed fields
    const updates = {};

    if (editedCustomer.name !== customerName) {
      updates.name = editedCustomer.name;
    }
    if (editedCustomer.email !== customerEmail) {
      updates.email = editedCustomer.email;
    }
    if (editedCustomer.phone !== customerPhone) {
      updates.phone = editedCustomer.phone;
    }
    if (editedCustomer.address !== customerAddress) {
      updates.address = editedCustomer.address;
    }

    // Only update if there are actual changes
    if (Object.keys(updates).length > 0) {
      const customerIdToUpdate = customer?.id || customer?.customer_id || customer?._id;
      console.log('Updating customer with field-level changes:', updates);
      updateCustomer(customerIdToUpdate, updates);
      // Edit mode exit handled by savingCustomer flag watcher
    } else {
      // No changes — just exit edit mode
      setIsEditingCustomer(false);
      setEditedCustomer({});
    }
  };

  const handleCancelEdit = () => {
    // Discard changes and exit edit mode
    setIsEditingCustomer(false);
    setEditedCustomer({});
  };

  const handleDeleteCustomer = () => {
    if (!customer || deletingCustomer) return;

    const customerIdToDelete = customer.id || customer.customer_id || customer._id;
    const customerNameForConfirm = customer.name || customer.full_name || customer.customer_name || 'this customer';

    if (window.confirm(`Are you sure you want to delete ${customerNameForConfirm}? This action cannot be undone.`)) {
      console.log('Deleting customer:', customerIdToDelete);
      deleteCustomer(customerIdToDelete);
      // Navigation handled by deletingCustomer flag watcher
    }
  };

  const handleCreateInvoice = () => {
    const customerName = customer?.name || customer?.full_name || customer?.customer_name;
    console.log('Create invoice for customer:', customerName);
    // TODO: Navigate to invoice creation
    // navigate('/invoices/new', { state: { customerId, customerName } });
  };

  const handleScheduleAppointment = () => {
    const customerId = customer?.id || customer?.customer_id || customer?._id;
    const customerName = customer?.name || customer?.full_name || customer?.customer_name;
    const customerEmail = customer?.email || customer?.email_address || '';
    const customerPhone = customer?.phone || customer?.phone_number || customer?.mobile || '';

    console.log('✅ Scheduling appointment for customer:', customerName);

    // Navigate to calendar with pre-selected customer data
    navigate('/calendar', {
      state: {
        preSelectedCustomer: {
          id: customerId,
          name: customerName,
          email: customerEmail,
          phone: customerPhone
        },
        openEventPanel: true,
        eventType: 'service'
      }
    });
  };

  const handleViewAppointment = (appointment) => {
    const appointmentId = appointment.id || appointment.appointment_id || appointment._id;
    console.log('View appointment:', appointmentId);
    navigate(`/appointments/${appointmentId}`);
  };

  const handleViewInvoice = (invoice) => {
    const invoiceId = invoice.id || invoice.invoice_id || invoice._id;
    console.log('View invoice:', invoiceId);
    navigate(`/invoices/${invoiceId}`);
  };

  // Get entries from store and transform for timeline display
  const entries = customerId ? getEntriesForCustomer(customerId) : [];
  const entriesLoading = customerId ? isLoadingEntriesForCustomer(customerId) : false;

  // Transform entries into timeline display format
  const timelineEvents = useMemo(() => {
    // Helper to get styling based on entry type and action
    const getEntryStyle = (entryType, action) => {
      // Entry type specific styling
      const typeStyles = {
        note: {
          icon: StickyNote,
          color: 'text-blue-400',
          bgColor: 'bg-blue-400/10',
          borderColor: 'border-blue-400/30'
        },
        document: {
          icon: File,
          color: 'text-purple-400',
          bgColor: 'bg-purple-400/10',
          borderColor: 'border-purple-400/30'
        },
        tag: {
          icon: Tag,
          color: 'text-amber-400',
          bgColor: 'bg-amber-400/10',
          borderColor: 'border-amber-400/30'
        },
        field_change: {
          icon: Edit,
          color: 'text-cyan-400',
          bgColor: 'bg-cyan-400/10',
          borderColor: 'border-cyan-400/30'
        },
        automation_action: {
          icon: Zap,
          color: 'text-yellow-400',
          bgColor: 'bg-yellow-400/10',
          borderColor: 'border-yellow-400/30'
        },
        workflow: {
          icon: GitBranch,
          color: 'text-indigo-400',
          bgColor: 'bg-indigo-400/10',
          borderColor: 'border-indigo-400/30'
        },
        customer: {
          icon: UserPlus,
          color: 'text-green-400',
          bgColor: 'bg-green-400/10',
          borderColor: 'border-green-400/30'
        }
      };

      // Action-based color overrides for removed actions
      if (action === 'removed') {
        return {
          ...typeStyles[entryType] || typeStyles.customer,
          icon: XCircle,
          color: 'text-red-400',
          bgColor: 'bg-red-400/10',
          borderColor: 'border-red-400/30'
        };
      }

      return typeStyles[entryType] || {
        icon: Activity,
        color: 'text-slate-400',
        bgColor: 'bg-slate-400/10',
        borderColor: 'border-slate-400/30'
      };
    };

    // Helper to generate title from entry
    const getEntryTitle = (entry) => {
      const actionLabels = {
        added: 'Added',
        modified: 'Updated',
        removed: 'Removed'
      };
      const typeLabels = {
        note: 'Note',
        document: 'Document',
        tag: 'Tag',
        field_change: 'Field',
        automation_action: 'Automation',
        workflow: 'Workflow',
        customer: 'Customer'
      };

      const action = actionLabels[entry.action] || entry.action;
      const type = typeLabels[entry.entry_type] || entry.entry_type;

      return `${type} ${action}`;
    };

    // Helper to generate description from entry metadata
    const getEntryDescription = (entry) => {
      const meta = entry.metadata || {};

      switch (entry.entry_type) {
        case 'note':
          return meta.title || meta.note_type || 'Note activity';
        case 'document':
          return meta.file_name || meta.document_type || 'Document activity';
        case 'tag':
          return meta.tag_name ? `Tag: ${meta.tag_name}` : 'Tag activity';
        case 'field_change':
          if (meta.field_label) {
            return `${meta.field_label}: ${meta.old_value || 'empty'} → ${meta.new_value || 'empty'}`;
          }
          return 'Field updated';
        case 'automation_action':
          return meta.action_type || meta.workflow_name || 'Automation executed';
        case 'workflow':
          if (meta.event === 'entered') {
            return `Entered ${meta.workflow_name || 'workflow'}`;
          } else if (meta.event === 'moved') {
            return `Moved to ${meta.stage_name || 'new stage'}`;
          } else if (meta.event === 'exited') {
            return `Exited ${meta.workflow_name || 'workflow'}`;
          }
          return meta.workflow_name || 'Workflow activity';
        case 'customer':
          return 'Customer record activity';
        default:
          return 'Activity logged';
      }
    };

    // Helper to get actor label
    const getActorLabel = (entry) => {
      switch (entry.actor_type) {
        case 'user':
          return 'User';
        case 'automation':
          return 'Automation';
        case 'system':
          return 'System';
        default:
          return null;
      }
    };

    // Transform entries to timeline events
    return entries.map(entry => {
      const style = getEntryStyle(entry.entry_type, entry.action);

      return {
        id: entry.id,
        type: entry.entry_type,
        title: getEntryTitle(entry),
        description: getEntryDescription(entry),
        timestamp: entry.created_at,
        action: entry.action,
        actorType: entry.actor_type,
        actorLabel: getActorLabel(entry),
        icon: style.icon,
        color: style.color,
        bgColor: style.bgColor,
        borderColor: style.borderColor,
        references: entry.references,
        metadata: entry.metadata
      };
    });
    // Note: entries are already sorted by created_at desc from the backend
  }, [entries]);

  // Connection status warning
  if (!isConnected) {
    return (
      <PageContainer>
        <Alert variant="destructive" className="mb-6">
          <AlertDescription>
            Not connected to server. Please check your connection and try again.
          </AlertDescription>
        </Alert>
        <div className="text-center py-16">
          <h2 className="text-2xl font-bold mb-2">Connection Required</h2>
          <p className="text-muted-foreground mb-6">
            Please check your internet connection and try again.
          </p>
          <Button onClick={handleBack}>
            Back to Customers
          </Button>
        </div>
      </PageContainer>
    );
  }

  // Loading state
  if (selectedCustomerLoading && !selectedCustomer) {
    return (
      <PageContainer>
        <div className="flex items-center justify-center min-h-96">
          <div className="text-center">
            <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4" />
            <p className="text-muted-foreground">Loading customer details...</p>
          </div>
        </div>
      </PageContainer>
    );
  }

  // Error state
  if (selectedCustomerError && !selectedCustomer) {
    return (
      <PageContainer>
        <Alert variant="destructive" className="mb-6">
          <AlertDescription>{selectedCustomerError}</AlertDescription>
        </Alert>
        <div className="text-center py-16">
          <h2 className="text-2xl font-bold mb-2">Unable to Load Customer</h2>
          <p className="text-muted-foreground mb-6">{selectedCustomerError}</p>
          <div className="flex gap-3 justify-center">
            <Button onClick={() => loadCustomerDetails(customerId, {
              includeAppointments: true,
              includeInvoices: true,
              includeStatistics: true
            })}>
              Try Again
            </Button>
            <Button variant="outline" onClick={handleBack}>
              Back to Customers
            </Button>
          </div>
        </div>
      </PageContainer>
    );
  }

  // Customer not found
  if (!selectedCustomerLoading && !customer) {
    return (
      <PageContainer>
        <div className="text-center py-16">
          <h2 className="text-2xl font-bold mb-2">Customer Not Found</h2>
          <p className="text-muted-foreground mb-6">
            The customer you're looking for doesn't exist or has been removed.
          </p>
          <Button onClick={handleBack}>
            Back to Customers
          </Button>
        </div>
      </PageContainer>
    );
  }

  const customerName = customer?.name || customer?.full_name || customer?.customer_name || 'Unknown Customer';
  const customerEmail = customer?.email || customer?.email_address || '';
  const customerPhone = customer?.phone || customer?.phone_number || customer?.mobile || '';
  const customerAddress = customer?.address || '';
  const smsConsent = customer?.sms_consent || false;
  const smsConsentDate = customer?.sms_consent_date;
  const smsConsentMethod = customer?.sms_consent_method;
  const smsOptOutDate = customer?.sms_opt_out_date;
  const hasPortalAccess = customer?.has_password || false;
  const lastLogin = customer?.last_login;
  const customerSince = customer?.created_at ? formatDate(customer.created_at) : 'Unknown';

  // Navigation sections
  const navSections = [
    { id: 'overview', label: 'Overview' },
    { id: 'appointments', label: 'Appointments' },
    { id: 'invoices', label: 'Invoices' },
    { id: 'activity', label: 'Activity' },
    { id: 'communication', label: 'Communication' },
    { id: 'referrals', label: 'Referrals' }
  ];

  return (
    <PageContainer heightMode="viewport" maxWidth="full" padding="none">
      <div className="h-full flex flex-col bg-gradient-to-tl from-[#0a0a0a] from-0% via-[#0b0b0f] via-20% via-[#0c0c18] via-40% via-[#0b0b12] via-60% via-[#0b0b0e] via-80% to-[#0a0a0a] to-100%">
        {/* Main Content */}
        <div className="flex-1 overflow-hidden">
          <div className="h-full pt-12 pl-36 pr-36 flex flex-col">
            {/* Action Buttons */}
            <div className="flex justify-end mb-2">
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={handleBack}>
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Back
                </Button>
                <Button variant="outline" size="sm" onClick={handleEditCustomer}>
                  <Edit className="w-4 h-4 mr-2" />
                  Edit
                </Button>
                <Button variant="destructive" size="sm" onClick={handleDeleteCustomer} disabled={deletingCustomer || savingCustomer}>
                  <Trash2 className="w-4 h-4 mr-2" />
                  {deletingCustomer ? 'Deleting...' : 'Delete'}
                </Button>
              </div>
            </div>

            {/* Header */}
            <div className="mb-6">
              <div className="space-y-2">
                <div className="flex items-center gap-3">
                  <h1 className="text-4xl font-bold text-foreground">{customerName}</h1>
                  {smsConsent && (
                    <Badge className="bg-green-500/20 text-green-300 border-green-500/30">
                      <MessageSquare className="w-3 h-3 mr-1" />
                      SMS Enabled
                    </Badge>
                  )}
                  {hasPortalAccess && (
                    <Badge className="bg-blue-500/20 text-blue-300 border-blue-500/30">
                      <User className="w-3 h-3 mr-1" />
                      Portal Access
                    </Badge>
                  )}
                </div>
                <div className="flex items-center gap-4 text-sm text-muted-foreground">
                  {customerEmail && (
                    <div className="flex items-center gap-1.5">
                      <Mail className="w-4 h-4" />
                      <span>{customerEmail}</span>
                    </div>
                  )}
                  {customerPhone && (
                    <div className="flex items-center gap-1.5">
                      <Phone className="w-4 h-4" />
                      <span>{customerPhone}</span>
                    </div>
                  )}
                  <div className="flex items-center gap-1.5">
                    <Calendar className="w-4 h-4" />
                    <span>Customer since {customerSince}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Error display */}
            {selectedCustomerError && customer && (
              <Alert variant="destructive" className="mb-6">
                <AlertDescription>{selectedCustomerError}</AlertDescription>
              </Alert>
            )}

            {/* Side Nav and Content */}
            <div className="flex-1 flex min-h-0">
              {/* Side Navigation */}
              <div className="w-40 flex-shrink-0">
                <div className="space-y-1 sticky top-0">
                  {navSections.map((section) => (
                    <button
                      key={section.id}
                      onClick={() => scrollToSection(section.id)}
                      className={cn(
                        "block w-full text-left text-sm py-1 transition-colors",
                        activeSection === section.id
                          ? "text-foreground font-medium"
                          : "text-muted-foreground hover:text-foreground"
                      )}
                    >
                      {section.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Content - scrollable */}
              <div className="flex-1 min-w-0 overflow-y-auto pl-2 space-y-10 pb-12">
                {/* Overview Section */}
                <div ref={sectionRefs.overview} data-section="overview" className="scroll-mt-6 space-y-5">
                  <div>
                    <div className="flex items-center justify-between">
                      <h3 className="text-lg font-semibold flex items-center gap-2 text-foreground">
                        <User className="w-5 h-5" />
                        Customer Information
                      </h3>

                      {/* Edit / Save / Cancel buttons */}
                      {!isEditingCustomer ? (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={handleEditCustomer}
                          className="flex items-center gap-2"
                        >
                          <Edit className="w-4 h-4" />
                          Edit
                        </Button>
                      ) : (
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            onClick={handleSaveCustomer}
                            disabled={savingCustomer}
                          >
                            {savingCustomer ? 'Saving...' : 'Save Changes'}
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={handleCancelEdit}
                            disabled={savingCustomer}
                          >
                            Cancel
                          </Button>
                        </div>
                      )}
                    </div>
                    <p className="text-sm text-muted-foreground mt-2 leading-relaxed">
                      Complete profile and contact details for this customer
                    </p>
                  </div>

                  <div className="flex items-start gap-6">
                    <Avatar className="h-20 w-20 shrink-0">
                      <AvatarFallback className="text-2xl font-semibold">
                        {getInitials(customerName)}
                      </AvatarFallback>
                    </Avatar>
                    <div className="flex-1 space-y-4">
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label className="text-sm font-medium">Full Name</Label>
                          {!isEditingCustomer ? (
                            <p className="text-base">{customerName}</p>
                          ) : (
                            <Input
                              value={editedCustomer.name || ''}
                              onChange={(e) => setEditedCustomer({...editedCustomer, name: e.target.value})}
                              placeholder="Customer name"
                              className="bg-zinc-900 border border-zinc-700/50 shadow-[0_1px_2px_rgba(0,0,0,0.3),0_0_0_1px_rgba(255,255,255,0.05)] transition-all duration-200 focus-visible:border-zinc-500 focus-visible:shadow-[0_0_0_1px_rgba(255,255,255,0.2),0_4px_12px_rgba(255,255,255,0.1),0_2px_4px_rgba(0,0,0,0.4)] focus-visible:-translate-y-0.5"
                            />
                          )}
                        </div>
                        <div className="space-y-2">
                          <Label className="text-sm font-medium">Customer Since</Label>
                          <p className="text-base">{customerSince}</p>
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label className="text-sm font-medium flex items-center gap-2">
                            <Mail className="w-4 h-4" />
                            Email
                          </Label>
                          {!isEditingCustomer ? (
                            <p className="text-base text-muted-foreground">{customerEmail || 'N/A'}</p>
                          ) : (
                            <Input
                              type="email"
                              value={editedCustomer.email || ''}
                              onChange={(e) => setEditedCustomer({...editedCustomer, email: e.target.value})}
                              placeholder="customer@example.com"
                              className="bg-zinc-900 border border-zinc-700/50 shadow-[0_1px_2px_rgba(0,0,0,0.3),0_0_0_1px_rgba(255,255,255,0.05)] transition-all duration-200 focus-visible:border-zinc-500 focus-visible:shadow-[0_0_0_1px_rgba(255,255,255,0.2),0_4px_12px_rgba(255,255,255,0.1),0_2px_4px_rgba(0,0,0,0.4)] focus-visible:-translate-y-0.5"
                            />
                          )}
                        </div>
                        <div className="space-y-2">
                          <Label className="text-sm font-medium flex items-center gap-2">
                            <Phone className="w-4 h-4" />
                            Phone
                          </Label>
                          {!isEditingCustomer ? (
                            <p className="text-base text-muted-foreground">{customerPhone || 'N/A'}</p>
                          ) : (
                            <Input
                              type="tel"
                              value={editedCustomer.phone || ''}
                              onChange={(e) => setEditedCustomer({...editedCustomer, phone: e.target.value})}
                              placeholder="(555) 123-4567"
                              className="bg-zinc-900 border border-zinc-700/50 shadow-[0_1px_2px_rgba(0,0,0,0.3),0_0_0_1px_rgba(255,255,255,0.05)] transition-all duration-200 focus-visible:border-zinc-500 focus-visible:shadow-[0_0_0_1px_rgba(255,255,255,0.2),0_4px_12px_rgba(255,255,255,0.1),0_2px_4px_rgba(0,0,0,0.4)] focus-visible:-translate-y-0.5"
                            />
                          )}
                        </div>
                      </div>

                      <div className="space-y-2">
                        <Label className="text-sm font-medium flex items-center gap-2">
                          <MapPin className="w-4 h-4" />
                          Address
                        </Label>
                        {!isEditingCustomer ? (
                          <p className="text-base text-muted-foreground">{customerAddress || 'N/A'}</p>
                        ) : (
                          <Textarea
                            value={editedCustomer.address || ''}
                            onChange={(e) => setEditedCustomer({...editedCustomer, address: e.target.value})}
                            placeholder="Street address, city, state, zip"
                            rows={2}
                            className="bg-zinc-900 border border-zinc-700/50 shadow-[0_1px_2px_rgba(0,0,0,0.3),0_0_0_1px_rgba(255,255,255,0.05)] transition-all duration-200 focus-visible:border-zinc-500 focus-visible:shadow-[0_0_0_1px_rgba(255,255,255,0.2),0_4px_12px_rgba(255,255,255,0.1),0_2px_4px_rgba(0,0,0,0.4)] focus-visible:-translate-y-0.5"
                          />
                        )}
                      </div>

                      {customer?.how_heard && (
                        <div className="space-y-2">
                          <Label className="text-sm font-medium">How They Heard About Us</Label>
                          <p className="text-base text-muted-foreground capitalize">{customer.how_heard}</p>
                        </div>
                      )}

                      <div className="flex gap-2 pt-2">
                        <Button onClick={handleScheduleAppointment} size="sm">
                          <Calendar className="w-4 h-4 mr-2" />
                          Schedule Appointment
                        </Button>
                        <Button variant="outline" onClick={handleCreateInvoice} size="sm">
                          <FileText className="w-4 h-4 mr-2" />
                          Create Invoice
                        </Button>
                        <Button variant="outline" onClick={() => navigate(`/customers/${customerId}/content`)} size="sm">
                          <StickyNote className="w-4 h-4 mr-2" />
                          Notes & Documents
                        </Button>
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4 text-sm text-muted-foreground pt-2">
                    <div>
                      <span className="font-medium">Created:</span> {formatDateTime(customer?.created_at)}
                    </div>
                    <div>
                      <span className="font-medium">Last Updated:</span> {formatDateTime(customer?.updated_at)}
                    </div>
                  </div>
                </div>

                <Separator />

                {/* Appointments Section */}
                <div ref={sectionRefs.appointments} data-section="appointments" className="scroll-mt-6 space-y-5">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="text-lg font-semibold flex items-center gap-2 text-foreground">
                        <Calendar className="w-5 h-5" />
                        Appointment History
                        {appointments.length > 0 && (
                          <span className="ml-2 px-2 py-0.5 bg-primary/20 text-primary text-xs rounded-full font-normal">
                            {appointments.length}
                          </span>
                        )}
                      </h3>
                      <p className="text-sm text-muted-foreground mt-2 leading-relaxed">
                        All appointments for this customer
                      </p>
                    </div>
                    <Button size="sm" onClick={handleScheduleAppointment}>
                      <Plus className="w-4 h-4 mr-2" />
                      New Appointment
                    </Button>
                  </div>

                  {appointments.length === 0 ? (
                    <div className="text-center py-12">
                      <Calendar className="w-12 h-12 text-muted-foreground mx-auto mb-3 opacity-40" />
                      <p className="text-muted-foreground text-sm">
                        No appointments yet. Schedule the first one to get started.
                      </p>
                    </div>
                  ) : (
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Date & Time</TableHead>
                          <TableHead>Service</TableHead>
                          <TableHead>Status</TableHead>
                          <TableHead className="text-right">Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {appointments.map((appointment) => (
                          <TableRow
                            key={appointment.id || appointment.appointment_id}
                            className="cursor-pointer hover:bg-muted/50"
                            onClick={() => handleViewAppointment(appointment)}
                          >
                            <TableCell>
                              {formatDateTime(appointment.scheduled_datetime || appointment.appointment_date || appointment.date)}
                            </TableCell>
                            <TableCell>{appointment.service_type || appointment.title || 'Service'}</TableCell>
                            <TableCell>
                              <Badge
                                variant={
                                  appointment.status === 'completed' ? 'default' :
                                  appointment.status === 'cancelled' ? 'destructive' :
                                  'outline'
                                }
                                className={cn(
                                  appointment.status === 'completed' && 'bg-green-500/20 text-green-300 border-green-500/30',
                                  appointment.status === 'confirmed' && 'bg-blue-500/20 text-blue-300 border-blue-500/30'
                                )}
                              >
                                {appointment.status || 'pending'}
                              </Badge>
                            </TableCell>
                            <TableCell className="text-right">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleViewAppointment(appointment);
                                }}
                              >
                                View
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  )}
                </div>

                <Separator />

                {/* Invoices Section */}
                <div ref={sectionRefs.invoices} data-section="invoices" className="scroll-mt-6 space-y-5">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="text-lg font-semibold flex items-center gap-2 text-foreground">
                        <DollarSign className="w-5 h-5" />
                        Invoice History
                        {invoices.length > 0 && (
                          <span className="ml-2 px-2 py-0.5 bg-primary/20 text-primary text-xs rounded-full font-normal">
                            {invoices.length}
                          </span>
                        )}
                      </h3>
                      <p className="text-sm text-muted-foreground mt-2 leading-relaxed">
                        All invoices for this customer
                      </p>
                    </div>
                    <Button size="sm" onClick={handleCreateInvoice}>
                      <Plus className="w-4 h-4 mr-2" />
                      New Invoice
                    </Button>
                  </div>

                  {invoices.length === 0 ? (
                    <div className="text-center py-12">
                      <FileText className="w-12 h-12 text-muted-foreground mx-auto mb-3 opacity-40" />
                      <p className="text-muted-foreground text-sm">
                        No invoices yet. Create the first one to get started.
                      </p>
                    </div>
                  ) : (
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Invoice #</TableHead>
                          <TableHead>Date</TableHead>
                          <TableHead>Amount</TableHead>
                          <TableHead>Balance</TableHead>
                          <TableHead>Status</TableHead>
                          <TableHead className="text-right">Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {invoices.map((invoice) => (
                          <TableRow
                            key={invoice.id || invoice.invoice_id}
                            className="cursor-pointer hover:bg-muted/50"
                            onClick={() => handleViewInvoice(invoice)}
                          >
                            <TableCell className="font-medium">
                              #{invoice.invoice_number || invoice.id}
                            </TableCell>
                            <TableCell>
                              {formatDate(invoice.invoice_date || invoice.created_at || invoice.date)}
                            </TableCell>
                            <TableCell className="font-medium">
                              {formatCurrency(invoice.total_amount || invoice.total || invoice.amount)}
                            </TableCell>
                            <TableCell>
                              {invoice.balance_due > 0 ? (
                                <span className="text-amber-400">{formatCurrency(invoice.balance_due)}</span>
                              ) : (
                                <span className="text-emerald-400">Paid</span>
                              )}
                            </TableCell>
                            <TableCell>
                              <Badge
                                variant={
                                  invoice.status === 'paid' ? 'default' :
                                  invoice.status === 'overdue' ? 'destructive' :
                                  'outline'
                                }
                                className={cn(
                                  invoice.status === 'paid' && 'bg-green-500/20 text-green-300 border-green-500/30',
                                  invoice.status === 'sent' && 'bg-blue-500/20 text-blue-300 border-blue-500/30',
                                  invoice.status === 'draft' && 'bg-slate-500/20 text-slate-300 border-slate-500/30'
                                )}
                              >
                                {invoice.status || 'draft'}
                              </Badge>
                            </TableCell>
                            <TableCell className="text-right">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleViewInvoice(invoice);
                                }}
                              >
                                View
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  )}
                </div>

                <Separator />

                {/* Activity Section */}
                <div ref={sectionRefs.activity} data-section="activity" className="scroll-mt-6 space-y-5">
                  <div>
                    <h3 className="text-lg font-semibold flex items-center gap-2 text-foreground">
                      <Activity className="w-5 h-5" />
                      Activity Timeline
                      {timelineEvents.length > 0 && (
                        <span className="ml-2 px-2 py-0.5 bg-primary/20 text-primary text-xs rounded-full font-normal">
                          {timelineEvents.length} {timelineEvents.length === 1 ? 'Entry' : 'Entries'}
                        </span>
                      )}
                    </h3>
                    <p className="text-sm text-muted-foreground mt-2 leading-relaxed">
                      Complete history of notes, documents, tags, and automation activity
                    </p>
                  </div>

                  {entriesLoading ? (
                    <div className="text-center py-12">
                      <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-3" />
                      <p className="text-muted-foreground text-sm">
                        Loading activity...
                      </p>
                    </div>
                  ) : timelineEvents.length === 0 ? (
                    <div className="text-center py-12">
                      <Activity className="w-12 h-12 text-muted-foreground mx-auto mb-3 opacity-40" />
                      <p className="text-muted-foreground text-sm">
                        No activity to display yet.
                      </p>
                    </div>
                  ) : (
                    <div className="relative pl-8">
                      {/* Timeline Line */}
                      <div className="absolute left-[15px] top-0 bottom-0 w-px bg-border" />

                      {/* Timeline Items */}
                      <div className="space-y-4">
                        {timelineEvents.map((event, index) => {
                          const Icon = event.icon;
                          const isLast = index === timelineEvents.length - 1;

                          return (
                            <div
                              key={event.id}
                              className="relative group"
                            >
                              {/* Timeline Dot */}
                              <div
                                className={cn(
                                  "absolute -left-8 top-0 w-8 h-8 rounded-full flex items-center justify-center border-2 transition-all",
                                  event.borderColor,
                                  event.bgColor,
                                  isLast && "ring-4 ring-border/50"
                                )}
                              >
                                <Icon className={cn("w-4 h-4", event.color)} />
                              </div>

                              {/* Event Content */}
                              <div className="p-4 rounded-lg border border-border bg-zinc-900/50 hover:bg-zinc-900/80 transition-colors">
                                <div className="flex items-start justify-between mb-2">
                                  <h4 className="text-sm font-semibold">{event.title}</h4>
                                  <span className="text-xs text-muted-foreground whitespace-nowrap ml-4">
                                    {formatDateTime(event.timestamp)}
                                  </span>
                                </div>
                                <p className="text-sm text-muted-foreground mb-2">{event.description}</p>
                                <div className="flex gap-2 flex-wrap">
                                  <Badge variant="outline" className="text-xs capitalize">
                                    {event.type.replace('_', ' ')}
                                  </Badge>
                                  {event.actorLabel && (
                                    <Badge
                                      variant="outline"
                                      className={cn(
                                        "text-xs",
                                        event.actorType === 'automation' && "text-yellow-400 border-yellow-400/30",
                                        event.actorType === 'system' && "text-slate-400 border-slate-400/30",
                                        event.actorType === 'user' && "text-blue-400 border-blue-400/30"
                                      )}
                                    >
                                      {event.actorType === 'automation' && <Bot className="w-3 h-3 mr-1" />}
                                      {event.actorLabel}
                                    </Badge>
                                  )}
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>

                <Separator />

                {/* Communication Section */}
                <div ref={sectionRefs.communication} data-section="communication" className="scroll-mt-6 space-y-5">
                  <div>
                    <h3 className="text-lg font-semibold flex items-center gap-2 text-foreground">
                      <MessageSquare className="w-5 h-5" />
                      Communication & Access
                    </h3>
                    <p className="text-sm text-muted-foreground mt-2 leading-relaxed">
                      SMS consent, portal access, and communication preferences
                    </p>
                  </div>

                  {/* SMS Communication */}
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <Label className="text-sm font-medium flex items-center gap-2">
                        <MessageSquare className="w-4 h-4" />
                        SMS Communication
                      </Label>
                      {smsConsent ? (
                        <Badge className="bg-green-500/20 text-green-300 border-green-500/30">
                          <CheckCircle className="w-3 h-3 mr-1" />
                          Enabled
                        </Badge>
                      ) : (
                        <Badge variant="outline" className="text-slate-400 border-slate-600">
                          <XCircle className="w-3 h-3 mr-1" />
                          Disabled
                        </Badge>
                      )}
                    </div>

                    {smsConsent ? (
                      <div className="space-y-3 pl-6">
                        <div className="grid grid-cols-2 gap-4">
                          <div className="space-y-1">
                            <Label className="text-xs text-muted-foreground">Consent Method</Label>
                            <p className="text-sm">{getConsentMethodDisplay(smsConsentMethod)}</p>
                          </div>
                          <div className="space-y-1">
                            <Label className="text-xs text-muted-foreground">Consent Date</Label>
                            <p className="text-sm">{formatDate(smsConsentDate)}</p>
                          </div>
                        </div>

                        {smsOptOutDate && (
                          <div className="flex items-center gap-2 text-sm text-amber-400">
                            <AlertCircle className="w-4 h-4" />
                            <span>Opted out on {formatDate(smsOptOutDate)}</span>
                          </div>
                        )}

                        <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-3 text-xs text-green-300">
                          <div className="flex gap-2">
                            <Shield className="w-4 h-4 flex-shrink-0 mt-0.5" />
                            <div>
                              <div className="font-medium mb-1">SMS messaging is enabled</div>
                              <div className="text-green-300/80">
                                You can send appointment reminders and updates via SMS to this customer.
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className="pl-6">
                        <div className="bg-slate-800/50 border border-slate-700/50 rounded-lg p-4 text-sm text-slate-400 text-center">
                          <MessageSquare className="w-8 h-8 mx-auto mb-2 opacity-40" />
                          <div>SMS consent not granted</div>
                          <div className="text-xs text-slate-500 mt-1">
                            Customer has not provided consent for SMS communication
                          </div>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Portal Access */}
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <Label className="text-sm font-medium flex items-center gap-2">
                        <User className="w-4 h-4" />
                        Customer Portal
                      </Label>
                      {hasPortalAccess ? (
                        <Badge className="bg-blue-500/20 text-blue-300 border-blue-500/30">
                          <CheckCircle className="w-3 h-3 mr-1" />
                          Active
                        </Badge>
                      ) : (
                        <Badge variant="outline" className="text-slate-400 border-slate-600">
                          <Clock className="w-3 h-3 mr-1" />
                          Not Set Up
                        </Badge>
                      )}
                    </div>

                    {hasPortalAccess ? (
                      <div className="space-y-3 pl-6">
                        <div className="grid grid-cols-2 gap-4">
                          <div className="space-y-1">
                            <Label className="text-xs text-muted-foreground">Account Status</Label>
                            <p className="text-sm text-blue-400">Active</p>
                          </div>
                          <div className="space-y-1">
                            <Label className="text-xs text-muted-foreground">Last Login</Label>
                            <p className="text-sm">
                              {lastLogin ? formatDateTime(lastLogin) : 'Never logged in'}
                            </p>
                          </div>
                        </div>

                        <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-3 text-xs text-blue-300">
                          <div className="flex gap-2">
                            <Shield className="w-4 h-4 flex-shrink-0 mt-0.5" />
                            <div>
                              <div className="font-medium mb-1">Portal access is active</div>
                              <div className="text-blue-300/80">
                                Customer can log in to view appointments, invoices, and make bookings.
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className="pl-6">
                        <div className="bg-slate-800/50 border border-slate-700/50 rounded-lg p-4 text-sm text-slate-400 text-center">
                          <User className="w-8 h-8 mx-auto mb-2 opacity-40" />
                          <div>Portal access not set up</div>
                          <div className="text-xs text-slate-500 mt-1">
                            Customer has not completed account setup
                          </div>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Contact Information Summary */}
                  <div className="space-y-3">
                    <Label className="text-sm font-medium flex items-center gap-2">
                      <Mail className="w-4 h-4" />
                      Contact Information
                    </Label>
                    <div className="pl-6 grid grid-cols-2 gap-4">
                      <div className="space-y-1">
                        <Label className="text-xs text-muted-foreground">Email</Label>
                        <p className="text-sm break-all">{customerEmail || 'N/A'}</p>
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs text-muted-foreground">Phone</Label>
                        <p className="text-sm">{customerPhone || 'N/A'}</p>
                      </div>
                      {customer?.how_heard && (
                        <div className="space-y-1">
                          <Label className="text-xs text-muted-foreground">How They Heard</Label>
                          <p className="text-sm capitalize">{customer.how_heard}</p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                <Separator />

                {/* Referrals Section */}
                <div ref={sectionRefs.referrals} data-section="referrals" className="scroll-mt-6 space-y-5">
                  <div>
                    <h3 className="text-lg font-semibold flex items-center gap-2 text-foreground">
                      <Gift className="w-5 h-5" />
                      Referral Program
                    </h3>
                    <p className="text-sm text-muted-foreground mt-2 leading-relaxed">
                      Customer's referral code, link, and referral history
                    </p>
                  </div>

                  {/* Referral Code & Link */}
                  {customer?.referral_code ? (
                    <div className="space-y-4">
                      {/* Referral Code Display */}
                      <div className="space-y-3">
                        <Label className="text-sm font-medium flex items-center gap-2">
                          <Tag className="w-4 h-4" />
                          Referral Code
                        </Label>
                        <div className="pl-6">
                          <div className="flex items-center gap-3">
                            <code className="px-4 py-2 bg-primary/10 border border-primary/30 rounded-lg text-lg font-mono font-bold text-primary">
                              {customer.referral_code}
                            </code>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => {
                                navigator.clipboard.writeText(customer.referral_code);
                              }}
                            >
                              <Copy className="w-4 h-4 mr-1" />
                              Copy
                            </Button>
                          </div>
                        </div>
                      </div>

                      {/* Referral Link (if widget is configured) */}
                      {customer?.referral_link && (
                        <div className="space-y-3">
                          <Label className="text-sm font-medium flex items-center gap-2">
                            <Link className="w-4 h-4" />
                            Referral Link
                          </Label>
                          <div className="pl-6">
                            <div className="flex items-center gap-2">
                              <Input
                                value={customer.referral_link}
                                readOnly
                                className="flex-1 text-sm font-mono bg-slate-800/50"
                              />
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => {
                                  navigator.clipboard.writeText(customer.referral_link);
                                }}
                              >
                                <Copy className="w-4 h-4 mr-1" />
                                Copy
                              </Button>
                            </div>
                            <p className="text-xs text-muted-foreground mt-2">
                              Share this link with the customer so they can refer friends and family
                            </p>
                          </div>
                        </div>
                      )}

                      {/* Referral Statistics */}
                      {(customer?.referral_stats?.total_referrals > 0 ||
                        customer?.referral_stats?.total_credits > 0) && (
                        <div className="space-y-3">
                          <Label className="text-sm font-medium flex items-center gap-2">
                            <Activity className="w-4 h-4" />
                            Referral Stats
                          </Label>
                          <div className="pl-6 grid grid-cols-3 gap-4">
                            <div className="bg-slate-800/50 border border-slate-700/50 rounded-lg p-3 text-center">
                              <div className="text-2xl font-bold text-foreground">
                                {customer?.referral_stats?.total_referrals || 0}
                              </div>
                              <div className="text-xs text-muted-foreground mt-1">Total Referrals</div>
                            </div>
                            <div className="bg-slate-800/50 border border-slate-700/50 rounded-lg p-3 text-center">
                              <div className="text-2xl font-bold text-green-400">
                                {customer?.referral_stats?.completed_referrals || 0}
                              </div>
                              <div className="text-xs text-muted-foreground mt-1">Completed</div>
                            </div>
                            <div className="bg-slate-800/50 border border-slate-700/50 rounded-lg p-3 text-center">
                              <div className="text-2xl font-bold text-primary">
                                ${customer?.referral_stats?.total_credits?.toFixed(2) || '0.00'}
                              </div>
                              <div className="text-xs text-muted-foreground mt-1">Credits Earned</div>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Available Credits */}
                      {customer?.available_credits?.length > 0 && (
                        <div className="space-y-3">
                          <Label className="text-sm font-medium flex items-center gap-2">
                            <DollarSign className="w-4 h-4" />
                            Available Credits
                          </Label>
                          <div className="pl-6 space-y-2">
                            {customer.available_credits.map((credit, index) => (
                              <div
                                key={credit.id || index}
                                className="flex items-center justify-between p-3 bg-green-500/10 border border-green-500/30 rounded-lg"
                              >
                                <div>
                                  <div className="font-medium text-green-300">
                                    ${parseFloat(credit.amount).toFixed(2)} Credit
                                  </div>
                                  <div className="text-xs text-green-300/70">
                                    {credit.description || 'Referral reward'}
                                  </div>
                                </div>
                                <Badge className="bg-green-500/20 text-green-300 border-green-500/30">
                                  Available
                                </Badge>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="pl-0">
                      <div className="bg-slate-800/50 border border-slate-700/50 rounded-lg p-4 text-sm text-slate-400 text-center">
                        <Gift className="w-8 h-8 mx-auto mb-2 opacity-40" />
                        <div>No referral code assigned</div>
                        <div className="text-xs text-slate-500 mt-1">
                          Referral codes are generated automatically for new customers
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </PageContainer>
  );
};

export default CustomerDetails;
